/**
 * Точка входа витрины Lang · Ф2.2
 *
 * Порядок старта:
 *   1. поднять мост Telegram (ready → expand → высота → подписки на тему/вьюпорт);
 *   2. поставить светлую тему (дефолт), чтобы первый кадр не мигал;
 *   3. рукопожатие: POST /auth по подписи initData — там же онбординг нового
 *      члена семьи (создание + посев словаря; повторный вход ничего не создаёт);
 *   4. подтянуть /me и /progress, собрать оболочку и открыть кабинет.
 *
 * ⛔ Ядро и схему не трогаем — API только потребляем.
 */

import "./styles/tokens.css";
import "./styles/app.css";
import "./styles/reader.css";

import * as api from "./api";
import { showGate } from "./access";
import { showCalibration } from "./screens/calibration";
import { setLoader as setAudioLoader, unlock as unlockAudio } from "./audio";
import { applyTheme, initData, initTelegram, insideTelegram, platform } from "./tg";
import { noteClient, noteScreen } from "./feedback";
import { adoptSettings, loadFeedback, reapplyTheme, state, type ScreenName } from "./state";
import { go, mountShell, render } from "./router";
import { renderAdmin, refreshAdmin } from "./screens/admin";
import { renderAwards } from "./screens/awards";
import { renderCabinet } from "./screens/cabinet";
import { enterDanger, renderDanger } from "./screens/danger";
import { enterLibrary, leaveLibrary, renderLibrary } from "./screens/library";
import { enterSession, leaveSession, renderSession } from "./screens/session";
import { renderSettings } from "./screens/settings";
import { renderStats } from "./screens/stats";

const SCREENS: { name: ScreenName; render: (host: HTMLElement) => void }[] = [
  { name: "cabinet", render: renderCabinet },
  { name: "stats", render: renderStats },
  { name: "session", render: renderSession },
  { name: "awards", render: renderAwards },
  { name: "settings", render: renderSettings },
  // ⚠ Экран без пункта навигации: нижних кнопок по-прежнему пять, сюда ведёт карточка
  // в настройках. ⛔ Доступ решает не эта строка, а API (403 постороннему).
  { name: "admin", render: renderAdmin },
  // ⚠ Тоже без пункта навигации (окно 6): «Опасная зона» живёт в глубине настроек —
  // это первый из трёх слоёв защиты от случайного нажатия.
  { name: "danger", render: renderDanger },
  // ⚠ И читалка — тоже без пункта навигации (Фаза 3 «книги»): их по-прежнему ПЯТЬ,
  // как решили дизайн-ворота. Вход — карточкой в кабинете.
  { name: "library", render: renderLibrary },
];

/** Экран «не получилось» вместо белого листа: что случилось и что делать. */
function fail(title: string, lines: string[], retry = true): void {
  const shell = document.getElementById("shell");
  if (!shell) return;
  shell.innerHTML =
    '<main class="scroll"><div class="wrap"><div class="fail">' +
      `<h3>${title}</h3>` +
      lines.map((l) => `<p>${l}</p>`).join("") +
      (retry ? '<button type="button" id="retry">Попробовать снова</button>' : "") +
    "</div></div></main>";
  document.getElementById("retry")?.addEventListener("click", () => location.reload());
}

/** Тихо обновить цифры и перерисовать открытый экран (данные живут в ядре). */
async function refresh(): Promise<void> {
  try {
    const [me, prog, ach] = await Promise.all([api.me(), api.progress(), api.achievements()]);
    state.me = me;
    state.progress = prog;
    state.achievements = ach;
    render(state.screen);
  } catch {
    /* сеть мигнула — оставляем на экране прежние цифры, не пугаем пользователя */
  }
}

/**
 * Поднять приложение по уже полученному рукопожатию.
 * ⭐ Один и тот же путь у первого входа и у возврата после подписки — поэтому
 * «я подписался — проверить» открывает приложение БЕЗ перезахода.
 */
async function start(auth: api.AuthOut): Promise<void> {
  locked = false;
  if (auth.settings) adoptSettings(auth.settings);

  const [me, prog, ach] = await Promise.all([api.me(), api.progress(), api.achievements()]);
  state.me = me;
  state.progress = prog;
  state.achievements = ach;

  // ⭐⭐ Калибровка на старте. Пока она не пройдена, приложение не открываем ВООБЩЕ —
  // тем же приёмом, что замок окна 3: иначе человек уровня B1 неделю листает `be · have`
  // и уходит. ⛔ Кнопки «пропустить» нет; проходят и новые, и уже зашедшие.
  // Пройдя её, человек попадает сюда же (`start` зовётся заново) — но уже без калибровки.
  if (me.calibration_needed) {
    await showCalibration(() => start(auth));
    return;
  }

  // Окно 5: спрашивать ли оценку и какие у человека заявки — решает ядро, мы читаем.
  void loadFeedback();

  mountShell(SCREENS, (name) => {
    if (name === "session") {
      // ⚠ Автозапуск звука браузеры глушат до первого касания: нажатие «Учить» —
      // это и есть касание, поэтому именно здесь просим разрешение (отказ запомнится).
      unlockAudio();
      enterSession();                           // подход берём у ядра при входе на экран
    } else leaveSession();                      // ушли — гасим конфетти, похвалы и звук
    // Ф2.4: читающие экраны берут свежие цифры у ядра при каждом заходе — иначе
    // награда, полученная на занятии, появится в списке только после перезапуска.
    if (name === "cabinet" || name === "stats" || name === "awards") void refresh();
    if (name === "admin") refreshAdmin();
    // Окно 6: при каждом заходе — свежие цифры «что потеряешь» и чистое поле ввода.
    if (name === "danger") enterDanger();
    // Фаза 3 «книги»: полку берём у ядра при каждом заходе; уходя — снимаем класс
    // чтения, которым спрятана навигация, и гасим карточку слова.
    if (name === "library") enterLibrary(); else leaveLibrary();
    // Окно 5: приложение помнит, где человек был — это первая строка заявки.
    noteScreen(name);
  });
  go("cabinet");

  if (auth.created) {
    const added = auth.seeded?.subgroups.reduce((sum, s) => sum + s.added, 0) ?? 0;
    console.info(`[Lang] новый пользователь: посеяно слов ${added}`);
  }
  console.info(`[Lang] витрина 2.3 · платформа ${platform()} · ` +
               `в Telegram: ${insideTelegram() ? "да" : "нет (режим отладки)"}`);
}

/** Замок уже на экране — повторные 403 (их может прилететь пачка) его не перерисовывают. */
let locked = false;

function lock(access: api.Access): void {
  if (locked) return;
  locked = true;
  leaveSession();                               // гасим звук и таймеры текущего подхода
  showGate(access, recheck);
}

/** Кнопка «я подписался — проверить»: тот же вход. `true` = приложение уже открылось. */
async function recheck(): Promise<boolean> {
  const auth = await api.auth();
  if (auth.access && !auth.access.allowed) return false;
  await start(auth);
  return true;
}

async function boot(): Promise<void> {
  initTelegram(() => { if (state.themePref === "auto") reapplyTheme(); });
  // Окно 5: устройство и клиент — в контекст заявки, чтобы человек их не вводил.
  noteClient(`${platform()} · ${window.screen?.width ?? 0}×${window.screen?.height ?? 0}`,
             insideTelegram() ? `Telegram ${window.Telegram?.WebApp?.version ?? "?"}`
                              : "браузер (отладка)");
  applyTheme(state.themePref);
  // Озвучку тянет сам плеер, но подпись Telegram живёт в клиенте API — отдаём ему загрузчик.
  setAudioLoader(api.audioBlob);
  // Окно 3: отписался посреди работы → 403 на любом запросе → замок вместо экрана.
  api.handleAccessDenied(lock);

  if (!initData()) {
    fail("Нужен Telegram", [
      "Приложение узнаёт тебя по подписи Telegram — открой его из чата с ботом.",
      import.meta.env.DEV
        ? 'Для отладки в браузере: <code>python api.py --dev-token</code> → ' +
          "открой адрес с <code>?dev_init=…</code> или положи строку в " +
          "<code>web/.env.local</code> как <code>VITE_DEV_INIT_DATA</code>."
        : "",
    ].filter(Boolean), false);
    return;
  }

  try {
    const auth = await api.auth();
    // Окно 3: нет доступа — приложение не открываем ВООБЩЕ (ни навигации, ни экранов).
    if (auth.access && !auth.access.allowed) {
      locked = true;
      showGate(auth.access, recheck);
      return;
    }
    await start(auth);
  } catch (e) {
    const err = e as api.ApiError;
    if (err.status === 0) {
      fail("Сервер не отвечает", [
        "Витрина открылась, но API не поднят.",
        "Запусти на ПК: <code>python core/api.py --serve</code>.",
      ]);
    } else if (err.status === 401) {
      fail("Не удалось опознать", [
        "Подпись Telegram не принята.",
        "Проверь, что <code>TELEGRAM_BOT_TOKEN</code> в <code>.env</code> — от того самого бота, " +
        "из которого открыто приложение.",
      ]);
    } else {
      // ⛔ Код ошибки человеку не показываем (правка 26.07): на старте это была
      // «Ошибка 500» — она не говорит ему ничего и починить её он не может.
      fail("Что-то пошло не так", [
        api.humanMessage(e, "Связь пропала — приложение не открылось"),
        "Попробуй ещё раз: если сервер на месте, всё поднимется само.",
      ]);
    }
  }
}

void boot();
